import {
  BANK_ACCOUNT_TYPES,
  type BankAccountType,
  LABELS,
  maskAccountNumber,
  todayInTimezone,
} from '@mm/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Landmark, Pencil, Unlink } from 'lucide-react';
import { useState } from 'react';
import { api, ApiError } from '../../api/client';
import { Button } from '../../components/ui/Button';
import { Badge, Card, EmptyState } from '../../components/ui/display';
import { Field, Input, Select, Textarea } from '../../components/ui/inputs';
import { Modal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/Toast';
import { DataTable } from '../../tables/DataTable';
import { formatDate } from '../../utils/format';
import { useAuth } from '../auth/AuthProvider';

interface BankLink {
  id: string;
  bankName: string;
  accountNumber: string;
  /** Null only on links made before these fields existed. */
  accountHolderName: string | null;
  accountType: BankAccountType | null;
  linkedOn: string;
  unlinkedOn: string | null;
  status: 'ACTIVE' | 'REMOVED';
  notes: string | null;
  linkedBy: { id: string; name: string } | null;
  unlinkedBy: { id: string; name: string } | null;
}

const key = (accountId: string) => ['revenue-account-banks', accountId] as const;

function errorText(err: unknown) {
  if (!(err instanceof ApiError)) return 'Saving failed';
  return err.errors[0]?.message ?? err.message;
}

const typeLabel = (t: BankAccountType | null) => (t ? LABELS.bankAccountType[t] : null);
const notRecorded = <span className="text-slate-400">Not recorded</span>;

/**
 * Indian bank account that receives this account's payouts: bank, account
 * holder, account number and type. Changing it keeps the previous link in
 * the history; "Edit details" corrects the holder or type of the current one.
 */
export function BankAccountCard({
  accountId,
  accountName,
}: {
  accountId: string;
  accountName: string;
}) {
  const { can } = useAuth();
  const canWrite = can('masters.write');
  const [linking, setLinking] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const [editing, setEditing] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: key(accountId),
    queryFn: () =>
      api.get<{ current: BankLink | null; history: BankLink[] }>(
        `/revenue-accounts/${accountId}/bank-accounts`,
      ),
  });
  const current = data?.current ?? null;

  return (
    <Card
      title="Payout bank account"
      description="Indian bank account that receives this account's payouts. Changes are kept in the history."
      bodyClassName="p-0"
      actions={
        canWrite ? (
          <>
            {current && (
              <>
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Pencil className="size-4" />}
                  onClick={() => setEditing(true)}
                >
                  Edit details
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Unlink className="size-4" />}
                  onClick={() => setUnlinking(true)}
                >
                  Unlink
                </Button>
              </>
            )}
            <Button
              size="sm"
              icon={<Landmark className="size-4" />}
              onClick={() => setLinking(true)}
            >
              {current ? 'Change bank account' : 'Link bank account'}
            </Button>
          </>
        ) : undefined
      }
    >
      {current && (
        <div className="flex flex-wrap items-center gap-x-8 gap-y-2 border-b border-slate-100 px-5 py-4">
          <div>
            <p className="text-xs text-slate-500">Bank</p>
            <p className="font-medium text-slate-900">{current.bankName}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Account holder</p>
            <p className="text-slate-900">{current.accountHolderName ?? notRecorded}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Account number</p>
            <p className="num font-mono text-slate-900">{current.accountNumber}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Account type</p>
            <p className="text-slate-900">{typeLabel(current.accountType) ?? notRecorded}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Linked since</p>
            <p className="text-slate-900">{formatDate(current.linkedOn)}</p>
          </div>
        </div>
      )}
      <DataTable<BankLink>
        tableId="bank-account"
        rows={data?.history}
        loading={isLoading}
        rowKey={(r) => r.id}
        columns={[
          { key: 'bankName', header: 'Bank', render: (r) => r.bankName },
          {
            key: 'holder',
            header: 'Account holder',
            render: (r) => r.accountHolderName ?? notRecorded,
          },
          {
            key: 'accountNumber',
            header: 'Account number',
            render: (r) => <span className="font-mono text-xs">{r.accountNumber}</span>,
          },
          {
            key: 'accountType',
            header: 'Type',
            render: (r) => typeLabel(r.accountType) ?? notRecorded,
          },
          { key: 'linkedOn', header: 'From', render: (r) => formatDate(r.linkedOn) },
          {
            key: 'unlinkedOn',
            header: 'Until',
            render: (r) => (r.unlinkedOn ? formatDate(r.unlinkedOn) : '—'),
          },
          {
            key: 'by',
            header: 'Changed by',
            render: (r) => r.linkedBy?.name ?? 'System',
          },
          {
            key: 'status',
            header: 'Status',
            render: (r) => (
              <Badge tone={r.status === 'ACTIVE' ? 'green' : 'slate'}>
                {r.status === 'ACTIVE' ? 'Current' : LABELS.assignmentStatus.REMOVED}
              </Badge>
            ),
          },
        ]}
        empty={
          <EmptyState
            title="No bank account linked"
            description={canWrite ? 'Link the bank account that receives the payouts.' : undefined}
          />
        }
      />
      {linking && (
        <LinkModal
          accountId={accountId}
          accountName={accountName}
          current={current}
          onClose={() => setLinking(false)}
        />
      )}
      {editing && current && (
        <EditDetailsModal
          accountId={accountId}
          current={current}
          onClose={() => setEditing(false)}
        />
      )}
      {unlinking && current && (
        <UnlinkModal accountId={accountId} current={current} onClose={() => setUnlinking(false)} />
      )}
    </Card>
  );
}

function useInvalidate(accountId: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: key(accountId) });
    void queryClient.invalidateQueries({ queryKey: ['resource'] });
    void queryClient.invalidateQueries({ queryKey: ['month-end'] });
  };
}

function LinkModal({
  accountId,
  accountName,
  current,
  onClose,
}: {
  accountId: string;
  accountName: string;
  current: BankLink | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const invalidate = useInvalidate(accountId);
  const [bankName, setBankName] = useState('');
  const [accountHolderName, setAccountHolderName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountType, setAccountType] = useState('');
  const [linkedOn, setLinkedOn] = useState(todayInTimezone());
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () =>
      api.post(`/revenue-accounts/${accountId}/bank-accounts`, {
        bankName,
        accountHolderName,
        accountNumber,
        accountType,
        linkedOn,
        notes,
      }),
    onSuccess: () => {
      toast.success(`Bank account linked to ${accountName}`);
      invalidate();
      onClose();
    },
    onError: (err) => setError(errorText(err)),
  });

  return (
    <Modal
      open
      size="sm"
      onClose={onClose}
      title={current ? 'Change bank account' : 'Link bank account'}
      description={
        current
          ? `${current.bankName} ${maskAccountNumber(current.accountNumber)} stays in the history until the day before the new one starts.`
          : `Bank account that receives payouts for ${accountName}.`
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={save.isPending}
            disabled={
              !bankName.trim() || !accountHolderName.trim() || !accountNumber.trim() || !accountType
            }
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Bank name" htmlFor="bank-name" required>
          <Input
            id="bank-name"
            value={bankName}
            placeholder="HDFC Bank"
            onChange={(e) => setBankName(e.target.value)}
          />
        </Field>
        <Field
          label="Account holder name"
          htmlFor="bank-holder"
          required
          help="As printed on the passbook / cheque"
        >
          <Input
            id="bank-holder"
            value={accountHolderName}
            placeholder="Example Apps Pvt Ltd"
            onChange={(e) => setAccountHolderName(e.target.value)}
          />
        </Field>
        <Field label="Account number" htmlFor="bank-number" required help="9–18 digits">
          <Input
            id="bank-number"
            inputMode="numeric"
            autoComplete="off"
            value={accountNumber}
            onChange={(e) => setAccountNumber(e.target.value)}
          />
        </Field>
        <AccountTypeField value={accountType} onChange={setAccountType} />
        <Field label="Payouts go here from" htmlFor="bank-from">
          <Input
            id="bank-from"
            type="date"
            value={linkedOn}
            min={current?.linkedOn}
            max={todayInTimezone()}
            onChange={(e) => setLinkedOn(e.target.value)}
          />
        </Field>
        <Field label="Notes" htmlFor="bank-notes">
          <Textarea
            id="bank-notes"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Field>
        {error && (
          <p className="text-sm text-rose-600" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}

function UnlinkModal({
  accountId,
  current,
  onClose,
}: {
  accountId: string;
  current: BankLink;
  onClose: () => void;
}) {
  const toast = useToast();
  const invalidate = useInvalidate(accountId);
  const [unlinkedOn, setUnlinkedOn] = useState(todayInTimezone());
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () =>
      api.delete(`/revenue-accounts/${accountId}/bank-accounts/current`, { unlinkedOn }),
    onSuccess: () => {
      toast.success('Bank account unlinked');
      invalidate();
      onClose();
    },
    onError: (err) => setError(errorText(err)),
  });
  return (
    <Modal
      open
      size="sm"
      onClose={onClose}
      title={`Unlink ${current.bankName} ${maskAccountNumber(current.accountNumber)}?`}
      description="It stays in the history."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" loading={save.isPending} onClick={() => save.mutate()}>
            Unlink
          </Button>
        </>
      }
    >
      <Field label="Last day payouts went here" htmlFor="bank-until">
        <Input
          id="bank-until"
          type="date"
          value={unlinkedOn}
          min={current.linkedOn}
          max={todayInTimezone()}
          onChange={(e) => setUnlinkedOn(e.target.value)}
        />
      </Field>
      {error && (
        <p className="mt-3 text-sm text-rose-600" role="alert">
          {error}
        </p>
      )}
    </Modal>
  );
}

function AccountTypeField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <Field label="Account type" htmlFor="bank-type" required>
      <Select id="bank-type" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="" disabled>
          Choose…
        </option>
        {BANK_ACCOUNT_TYPES.map((t) => (
          <option key={t} value={t}>
            {LABELS.bankAccountType[t]}
          </option>
        ))}
      </Select>
    </Field>
  );
}

/** Correct the holder name / type of the current link (no new history row). */
function EditDetailsModal({
  accountId,
  current,
  onClose,
}: {
  accountId: string;
  current: BankLink;
  onClose: () => void;
}) {
  const toast = useToast();
  const invalidate = useInvalidate(accountId);
  const [accountHolderName, setAccountHolderName] = useState(current.accountHolderName ?? '');
  const [accountType, setAccountType] = useState<string>(current.accountType ?? '');
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () =>
      api.put(`/revenue-accounts/${accountId}/bank-accounts/current`, {
        accountHolderName,
        accountType,
      }),
    onSuccess: () => {
      toast.success('Bank account details updated');
      invalidate();
      onClose();
    },
    onError: (err) => setError(errorText(err)),
  });
  return (
    <Modal
      open
      size="sm"
      onClose={onClose}
      title={`Edit ${current.bankName} ${maskAccountNumber(current.accountNumber)}`}
      description="Correct the account holder or type. To change the bank or account number, use “Change bank account” so the history is kept."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={save.isPending}
            disabled={!accountHolderName.trim() || !accountType}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Account holder name" htmlFor="edit-holder" required>
          <Input
            id="edit-holder"
            value={accountHolderName}
            onChange={(e) => setAccountHolderName(e.target.value)}
          />
        </Field>
        <AccountTypeField value={accountType} onChange={setAccountType} />
        {error && (
          <p className="text-sm text-rose-600" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
